import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  BarPlot,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsOnAxisClickHandler,
  ChartsReferenceLine,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  ResponsiveChartContainer,
} from '@mui/x-charts';
import type { BillingInsights, BillingStory } from '../../lib/billing-insights';
import { ChartLegend } from '../ChartLegend';
import { chartColors } from '../../theme';
import { formatRupiah } from '../../lib/format';
import { useLanguage } from '../../i18n';

function monthLabel(month: string, lang: string, withYear = false): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang === 'en' ? 'en-GB' : 'id-ID', {
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

/** Compact rupiah for the money axis: 1,5jt, 750rb. */
function compactRp(cents: number): string {
  const idr = cents / 100;
  if (idr >= 1_000_000) {
    const jt = idr / 1_000_000;
    return `${jt.toFixed(jt >= 10 ? 0 : 1).replace('.', ',')}jt`;
  }
  if (idr >= 1_000) return `${Math.round(idr / 1000)}rb`;
  return String(Math.round(idr));
}

/**
 * Money per month as stacked bars (collected, still owed) with the collection
 * rate as a line against the target. Hovering a month shows its numbers;
 * clicking it hands the month to `onMonthClick`.
 */
function CollectionTrend({
  insights,
  target,
  onMonthClick,
}: {
  insights: BillingInsights;
  target: number;
  onMonthClick?: (month: string) => void;
}) {
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const colors = chartColors(theme.palette.mode);
  const months = insights.trend.map((m) => m.month);
  const billed = (m: BillingInsights['trend'][number]) => m.collectedCents + m.outstandingCents > 0;
  const money = (v: number | null) => (v === null ? '—' : `Rp ${formatRupiah(v)}`);
  const maxBilled = Math.max(0, ...insights.trend.map((m) => m.collectedCents + m.outstandingCents));

  return (
    <Box sx={{ cursor: onMonthClick ? 'pointer' : 'default', mx: -1 }}>
      <ResponsiveChartContainer
        height={280}
        margin={{ top: 16, right: 48, bottom: 28, left: 56 }}
        xAxis={[{ id: 'months', scaleType: 'band', data: months, valueFormatter: (m: string, ctx) => monthLabel(m, lang, ctx.location === 'tooltip') }]}
        yAxis={[
          // Headroom above the bars keeps the rate line clear of them.
          { id: 'money', max: maxBilled > 0 ? maxBilled * 1.45 : undefined, valueFormatter: (v: number) => compactRp(v) },
          { id: 'pct', min: 0, max: 100, valueFormatter: (v: number) => `${v}%` },
        ]}
        series={[
          {
            type: 'bar',
            id: 'collected',
            label: t('bill.seriesCollected'),
            stack: 'money',
            yAxisId: 'money',
            color: colors.primary,
            data: insights.trend.map((m) => m.collectedCents),
            valueFormatter: money,
          },
          {
            type: 'bar',
            id: 'owed',
            label: t('bill.seriesOwed'),
            stack: 'money',
            yAxisId: 'money',
            color: colors.late,
            data: insights.trend.map((m) => m.outstandingCents),
            valueFormatter: money,
          },
          {
            type: 'line',
            id: 'rate',
            label: t('bill.seriesRate'),
            yAxisId: 'pct',
            color: theme.palette.text.primary,
            curve: 'linear',
            showMark: true,
            data: insights.trend.map((m) => (billed(m) ? Math.round(m.rate * 100) : null)),
            valueFormatter: (v: number | null) => (v === null ? '—' : `${v}%`),
          },
        ]}
      >
        <ChartsGrid horizontal />
        <BarPlot borderRadius={4} />
        <LinePlot />
        <MarkPlot />
        <ChartsReferenceLine
          axisId="pct"
          y={target * 100}
          label={t('bill.target', { pct: Math.round(target * 100) })}
          labelAlign="end"
          lineStyle={{ strokeDasharray: '5 4', stroke: theme.palette.text.secondary }}
          labelStyle={{ fontSize: 11, fill: theme.palette.text.secondary }}
        />
        <ChartsXAxis axisId="months" />
        <ChartsYAxis axisId="money" position="left" />
        <ChartsYAxis axisId="pct" position="right" />
        <ChartsAxisHighlight x="band" />
        <ChartsTooltip trigger="axis" />
        {onMonthClick ? (
          <ChartsOnAxisClickHandler
            onAxisClick={(_, data) => {
              const month = data ? months[data.dataIndex] : undefined;
              if (month) onMonthClick(month);
            }}
          />
        ) : null}
      </ResponsiveChartContainer>
      {/* The click hint on the left, the legend bottom-right. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ px: 1, mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {onMonthClick ? t('bill.clickMonthHint') : ''}
        </Typography>
        <ChartLegend
          items={[
            { label: t('bill.seriesCollected'), color: colors.primary },
            { label: t('bill.seriesOwed'), color: colors.late },
            { label: t('bill.seriesRate'), color: theme.palette.text.primary, kind: 'line' },
          ]}
        />
      </Stack>
    </Box>
  );
}

/** The chart's takeaway in words, so nobody has to work it out from the bars. */
function storyLine(story: BillingStory, target: number, t: (k: string, v?: Record<string, string | number>) => string): string {
  const parts: string[] = [];
  if (story.pastMonthCount > 0) {
    parts.push(
      story.monthsBelowTarget === 0
        ? t('bill.story.onTarget', { avg: Math.round(story.avgRatePastMonths * 100), target: Math.round(target * 100) })
        : t('bill.story.belowTarget', {
            avg: Math.round(story.avgRatePastMonths * 100),
            below: story.monthsBelowTarget,
            months: story.pastMonthCount,
            target: Math.round(target * 100),
          }),
    );
  }
  parts.push(
    story.housesBehind === 0
      ? t('bill.story.noArrears')
      : t('bill.story.arrears', { houses: story.housesBehind, amount: formatRupiah(story.owedCents) }),
  );
  return parts.join(' ');
}

function ArrearsCard({ insights, story }: { insights: BillingInsights; story: BillingStory | null }) {
  const { lang, t } = useLanguage();
  const { aging, arrears } = insights;
  const buckets = [
    { key: 'bill.aging1', cents: aging.upToOneMonth },
    { key: 'bill.aging2', cents: aging.twoToThreeMonths },
    { key: 'bill.aging3', cents: aging.overThreeMonths },
  ];

  return (
    <Card id="arrears" variant="outlined" sx={{ height: '100%', scrollMarginTop: 88 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('bill.arrearsTitle')}
            {arrears.length > 0 ? (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' · '}{t('bill.arrearsCount', { count: arrears.length })}
              </Box>
            ) : null}
          </Typography>
          <Button size="small" component={RouterLink} to="/reminders">{t('bill.sendReminders')}</Button>
        </Stack>

        {story && story.housesBehind > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('bill.arrearsContext', {
              amount: formatRupiah(story.owedCents),
              months: story.owedInMonthsOfFees.toLocaleString(lang === 'en' ? 'en-GB' : 'id-ID', { maximumFractionDigits: 1 }),
              houses: story.housesBehind,
              share: Math.round(story.housesBehindShare * 100),
            })}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {buckets.map((b, i) => (
            <Box key={b.key} sx={{ flex: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t(b.key)}</Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontVariantNumeric: 'tabular-nums', color: i === 2 && b.cents > 0 ? 'error.main' : 'text.primary' }}
              >
                Rp {formatRupiah(b.cents)}
              </Typography>
            </Box>
          ))}
        </Stack>

        {arrears.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('bill.noArrears')}</Typography>
        ) : (
          // Every house that owes, most months behind first. A long list scrolls
          // inside the card (about 10 rows visible) rather than stretching the page.
          <Stack
            divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}
            sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}
          >
            {arrears.map((a) => (
              <Stack key={a.unit_id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ width: 48 }}>{a.label}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color={a.monthsBehind >= 3 ? 'error.main' : 'text.primary'}>
                    {t('bill.monthsBehind', { count: a.monthsBehind })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('bill.since', { month: monthLabel(a.oldestMonth, lang, true) })}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  Rp {formatRupiah(a.outstandingCents)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function BillingInsightsPanel({
  insights,
  story,
  target,
  title,
  onMonthClick,
}: {
  insights: BillingInsights;
  story: BillingStory | null;
  target: number;
  /** Overrides the chart card title, e.g. for a single year. */
  title?: string;
  /** Called with 'YYYY-MM' when a month on the chart is clicked. */
  onMonthClick?: (month: string) => void;
}) {
  const { t } = useLanguage();
  return (
    // Stacked, each full width: the history first, the arrears list under it.
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600}>{title ?? t('bill.historyTitle')}</Typography>
          {story ? (
            <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>{storyLine(story, target, t)}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('bill.historySubtitle')}</Typography>
          )}
          <CollectionTrend insights={insights} target={target} onMonthClick={onMonthClick} />
        </CardContent>
      </Card>
      <ArrearsCard insights={insights} story={story} />
    </Stack>
  );
}
